// Entitlement hook (V3 scaffold).
// Resolves the current user's tier (free | pro). Reads an `entitlements` row mirrored
// from the Stripe webhook. Until configured / signed in, everyone is 'free'.
//
// Default posture: free tools are NEVER gated. Pro gating is opt-in per feature via
// `isPro`. When auth is unconfigured we intentionally return tier 'free' but callers
// should treat unconfigured as "do not block" (see AuthGate).

import { useEffect, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { monetizationEnabled } from '@/lib/flags';

export type Tier = 'free' | 'pro';

export interface Entitlement {
  tier: Tier;
  isPro: boolean;
  loading: boolean;
}

export function useEntitlement(): Entitlement {
  const { user } = useAuth();
  const [tier, setTier] = useState<Tier>('free');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const supabase = getSupabase();
    // Monetization off → everyone is free, no entitlement lookup.
    if (!monetizationEnabled || !supabase || !user) {
      setTier('free');
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    supabase
      .from('entitlements')
      .select('tier')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        setTier(data?.tier === 'pro' ? 'pro' : 'free');
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [user]);

  return { tier, isPro: tier === 'pro', loading };
}
